import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { createBlankReceivingEntry, createIngredientReceivingEntry } from '../store/slices/ingredientReceivingSlice';
import {
  createIngredientTraceCode,
  updateIngredientTraceCode,
  deleteIngredientTraceCode
} from '../store/slices/ingredientTraceCodesSlice';
import { fetchAllBatchRecordIngredients } from '../store/slices/batchRecordIngredientsSlice';
import { ShipmentTable } from '../features/ingredient-tracking/components/ShipmentTable';
import { ShipmentEntryRow } from '../features/ingredient-tracking/components/ShipmentEntryRow';
import { TrackingTable } from '../features/ingredient-tracking/components/TrackingTable';
import { ShipmentEntry } from '../types/shipment';
import { IngredientTraceCode } from '../types/ingredientTraceCode';
import { groupItemsBySection, getDefaultExpandedState, HaccpSection } from '../utils/haccpSectionUtils';
import { matchesSearchQuery } from '../utils/searchUtils';
import { HaccpSectionHeader } from '../components/haccp/HaccpSectionHeader';
import { TraceabilityProvider } from '../components/haccp/TraceabilityContext';

export const IngredientTracking: React.FC = () => {
  const dispatch = useAppDispatch();
  const receivingEntries = useAppSelector(state => state.ingredientReceiving.entries);
  const loading = useAppSelector(state => state.ingredientReceiving.loading);
  const suppliers = useAppSelector(state => state.suppliers.suppliers);
  const ingredientTraceCodes = useAppSelector(state => state.ingredientTraceCodes.traceCodes);
  const traceCodesLoading = useAppSelector(state => state.ingredientTraceCodes.loading);
  const traceCodesError = useAppSelector(state => state.ingredientTraceCodes.error);
  const fillings = useAppSelector(state => state.recipes.fillings);
  const components = useAppSelector(state => state.recipes.components);

  // Query param handling for deep-linking from AdminReview
  const [searchParams, setSearchParams] = useSearchParams();
  const targetTraceCodeId = searchParams.get('open');
  const targetTraceCodeField = searchParams.get('field');

  // Capture URL params in state so they survive the 100ms param-clearing effect
  const [savedTargetId] = useState(searchParams.get('open'));
  const [savedTargetField] = useState(searchParams.get('field'));
  const targetReceivingId = searchParams.get('openReceiving');
  const [savedReceivingField] = useState(searchParams.get('openReceiving') ? searchParams.get('field') : null);
  const [savedIsFlagged] = useState(searchParams.get('flagged') === 'true');

  // Find receiving date for auto-expand
  const targetReceivingDate = useMemo(() => {
    if (!targetReceivingId) return null;
    const entry = receivingEntries.find(e => e.id === targetReceivingId);
    return entry ? (entry.receivingDate || entry.createdAt.split('T')[0]) : null;
  }, [targetReceivingId, receivingEntries]);

  // State for managing new receiving log entry
  const [isAddingNewReceivingLog, setIsAddingNewReceivingLog] = useState(false);
  const [newReceivingEntry, setNewReceivingEntry] = useState<ShipmentEntry | null>(null);

  // State for section expansion (Trial/Monthly sections)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [sectionsInitialized, setSectionsInitialized] = useState(false);

  // State for single-accordion behavior within sections: track which date group is expanded (null = all collapsed)
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [receivingSearchTerm, setReceivingSearchTerm] = useState('');

  // Clear query params after processing
  useEffect(() => {
    if (targetTraceCodeId || targetReceivingId) {
      const timer = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [targetTraceCodeId, targetReceivingId, setSearchParams]);

  // Fetch all batch record ingredients for traceability resolution
  useEffect(() => {
    dispatch(fetchAllBatchRecordIngredients());
  }, [dispatch]);

  // Debug: Log when ingredientTraceCodes changes
  useEffect(() => {
    console.log('[DEBUG IngredientTracking] ingredientTraceCodes updated, count:', ingredientTraceCodes.length);
    console.log('[DEBUG IngredientTracking] ingredientTraceCodes:', ingredientTraceCodes);
  }, [ingredientTraceCodes]);

  // Filter receiving entries based on search
  const filteredReceivingEntries = useMemo(() => {
    if (!receivingSearchTerm.trim()) return receivingEntries;
    return receivingEntries.filter(entry => {
      const supplier = suppliers.find(s => s.id === entry.supplierId);
      return matchesSearchQuery(entry, receivingSearchTerm, [supplier?.supplierName || '']);
    });
  }, [receivingEntries, receivingSearchTerm, suppliers]);

  const sections = useMemo(() => {
    return groupItemsBySection(filteredReceivingEntries, (entry) => entry.receivingDate || entry.createdAt.split('T')[0]);
  }, [filteredReceivingEntries]);

  // Initialize expanded sections state when sections change
  useEffect(() => {
    if (sections.length > 0 && !sectionsInitialized) {
      setExpandedSections(getDefaultExpandedState(sections));
      setSectionsInitialized(true);
    }
  }, [sections, sectionsInitialized]);

  // Get grouped entries by date within each section
  const getGroupedEntriesForSection = (sectionItems: ShipmentEntry[]) => {
    const groups: Record<string, ShipmentEntry[]> = {};
    sectionItems.forEach(entry => {
      const date = entry.receivingDate || entry.createdAt.split('T')[0];
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(entry);
    });
    // Sort dates (newest first)
    return Object.entries(groups).sort(
      ([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime()
    );
  };

  // Find which section contains the target receiving entry for deep-linking
  const targetReceivingSectionKey = useMemo(() => {
    if (!targetReceivingId) return null;
    for (const section of sections) {
      if (section.items.some(e => e.id === targetReceivingId)) {
        return section.key;
      }
    }
    return null;
  }, [targetReceivingId, sections]);

  // Auto-expand section containing target entry for deep-linking
  useEffect(() => {
    if (targetReceivingSectionKey && !expandedSections[targetReceivingSectionKey]) {
      setExpandedSections(prev => ({ ...prev, [targetReceivingSectionKey]: true }));
    }
  }, [targetReceivingSectionKey]);

  // Auto-expand date within section for deep-linking
  useEffect(() => {
    if (targetReceivingDate && expandedDate !== targetReceivingDate) {
      setExpandedDate(targetReceivingDate);
    }
  }, [targetReceivingDate]);

  // Handler for "+ Add New Log" button
  const handleAddNewReceivingLog = () => {
    const blankEntry = createBlankReceivingEntry();
    // Get today's date in Central Time (America/Winnipeg)
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Winnipeg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(new Date());
    const year = parts.find(p => p.type === 'year')?.value || '';
    const month = parts.find(p => p.type === 'month')?.value || '';
    const day = parts.find(p => p.type === 'day')?.value || '';
    blankEntry.receivingDate = `${year}-${month}-${day}`;
    setNewReceivingEntry(blankEntry);
    setIsAddingNewReceivingLog(true);
  };

  // Handler for saving new receiving log entry
  const handleSaveNewReceivingLog = async (entry: ShipmentEntry) => {
    try {
      console.log('[DEBUG handleSaveNewReceivingLog] Dispatching createIngredientReceivingEntry...');
      const result = await dispatch(createIngredientReceivingEntry(entry)).unwrap();
      console.log('[DEBUG handleSaveNewReceivingLog] ✓ Entry created:', result);
      setIsAddingNewReceivingLog(false);
      setNewReceivingEntry(null);
    } catch (error) {
      console.error('[DEBUG handleSaveNewReceivingLog] ⚠️ Failed to create entry:', error);
      alert('Failed to create entry. Please try again.');
    }
  };

  // Handler for canceling new receiving log entry
  const handleCancelNewReceivingLog = () => {
    setIsAddingNewReceivingLog(false);
    setNewReceivingEntry(null);
  };

  const handleAddTrackingEntry = (entry: IngredientTraceCode) => {
    dispatch(createIngredientTraceCode(entry));
  };

  const handleUpdateTrackingEntry = (entry: IngredientTraceCode) => {
    dispatch(updateIngredientTraceCode(entry));
  };

  const handleDeleteTrackingEntry = (id: string) => {
    dispatch(deleteIngredientTraceCode(id));
  };

  // Handler for toggling section expansion
  const handleToggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  // Handler for toggling date group expansion (single-accordion behavior)
  const handleToggleDateGroup = (date: string) => {
    setExpandedDate(prevDate => prevDate === date ? null : date);
  };
  return (
    <TraceabilityProvider>
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden'
      }}
    >
      {/* Sticky Header Section */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10,
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <header
            style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <div>
              <h1 style={{ fontSize: '2rem', color: '#333', margin: '0 0 8px 0' }}>Ingredient Tracking</h1>
              <p style={{ color: '#666', fontSize: '1rem', margin: 0 }}>Track and manage ingredient inventory</p>
            </div>
          </header>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div
        data-scroll-container
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          backgroundColor: '#f8fafc'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Ingredient Receiving Log Section */}
          <div
            style={{
              border: '2px solid #ddd',
              borderRadius: '8px',
              marginBottom: '20px',
              backgroundColor: 'white',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Ingredient Receiving Log</h3>
              <button
                onClick={handleAddNewReceivingLog}
                disabled={isAddingNewReceivingLog}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '14px',
                  transition: 'background-color 0.2s',
                  backgroundColor: isAddingNewReceivingLog ? '#cccccc' : '#4CAF50',
                  color: 'white',
                  cursor: isAddingNewReceivingLog ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={e => {
                  if (!isAddingNewReceivingLog) {
                    e.currentTarget.style.backgroundColor = '#45a049';
                  }
                }}
                onMouseLeave={e => {
                  if (!isAddingNewReceivingLog) {
                    e.currentTarget.style.backgroundColor = '#4CAF50';
                  }
                }}
              >
                + Add New Log
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              {/* Search Receiving Entries */}
              {!isAddingNewReceivingLog && (
                <div style={{ marginBottom: '16px' }}>
                  <input
                    type="text"
                    placeholder="Search receiving entries..."
                    value={receivingSearchTerm}
                    onChange={(e) => setReceivingSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      outline: 'none',
                      boxSizing: 'border-box' as const
                    }}
                  />
                </div>
              )}

              {/* Loading State */}
              {loading && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                    color: '#666'
                  }}
                >
                  <p style={{ fontSize: '1rem', margin: 0 }}>Loading entries...</p>
                </div>
              )}

              {/* Empty State */}
              {!loading && sections.length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                    color: '#999'
                  }}
                >
                  <p style={{ fontSize: '1rem', margin: 0 }}>
                    No receiving entries recorded yet. Use the table below to add entries.
                  </p>
                </div>
              )}

              {/* No search results */}
              {!loading && receivingEntries.length > 0 && filteredReceivingEntries.length === 0 && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                    color: '#999'
                  }}
                >
                  <p style={{ fontSize: '1rem', margin: 0 }}>
                    No receiving entries match your search for "{receivingSearchTerm}".
                  </p>
                </div>
              )}

              {/* New Entry Form */}
              {isAddingNewReceivingLog && newReceivingEntry && (
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    backgroundColor: 'white',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      padding: '16px',
                      backgroundColor: '#f0f8ff',
                      borderBottom: '2px solid #e0e0e0'
                    }}
                  >
                    <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#333' }}>
                      New Entry for {(() => {
                        const dateStr = newReceivingEntry.receivingDate || '';
                        const [year, month, day] = dateStr.split('-').map(Number);
                        const date = new Date(year, month - 1, day);
                        return date.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        });
                      })()}
                    </h4>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '14px'
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Supplier Name</th>
                          <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Ingredient</th>
                          <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Lot #</th>
                          <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Quantity</th>
                          <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Condition Upon Arrival</th>
                          <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Receiving Temp</th>
                          <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Received By</th>
                          <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'left', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555' }}>Corrective Action</th>
                          <th style={{ padding: '12px 8px', backgroundColor: '#f8f9fa', fontWeight: '600', textAlign: 'center', borderBottom: '2px solid #e0e0e0', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.5px', color: '#555', width: '100px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <ShipmentEntryRow
                          entry={newReceivingEntry}
                          suppliers={suppliers}
                          isNew={true}
                          onSave={handleSaveNewReceivingLog}
                          onCancel={handleCancelNewReceivingLog}
                        />
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Sections with Grouped Entries by Date */}
              {!loading && sections.map(section => {
                const groupedEntries = getGroupedEntriesForSection(section.items);
                return (
                  <div key={section.key} style={{ marginBottom: '16px' }}>
                    <HaccpSectionHeader
                      label={section.label}
                      itemCount={section.items.length}
                      isExpanded={expandedSections[section.key] ?? !section.isTrialSection}
                      isTrialSection={section.isTrialSection}
                      onToggle={() => handleToggleSection(section.key)}
                    />
                    {(expandedSections[section.key] ?? !section.isTrialSection) && (
                      <div style={{ paddingLeft: '8px' }}>
                        {groupedEntries.map(([date, entries]) => (
                          <ShipmentTable
                            key={date}
                            receivingDate={date}
                            entries={entries}
                            suppliers={suppliers}
                            isExpanded={expandedDate === date}
                            onToggle={() => handleToggleDateGroup(date)}
                            targetEntryId={targetReceivingId || undefined}
                            targetField={savedReceivingField || undefined}
                            isFlagged={savedIsFlagged}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ingredient Trace Code Log Section */}
          <div
            style={{
              border: '2px solid #ddd',
              borderRadius: '8px',
              marginBottom: '20px',
              backgroundColor: 'white',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #ddd'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Ingredient Trace Code Log</h3>
            </div>
            <div style={{ padding: '20px' }}>
              <TrackingTable
                entries={ingredientTraceCodes}
                suppliers={suppliers}
                shipmentEntries={receivingEntries}
                fillings={fillings}
                components={components}
                loading={traceCodesLoading}
                error={traceCodesError}
                onAdd={handleAddTrackingEntry}
                onUpdate={handleUpdateTrackingEntry}
                onDelete={handleDeleteTrackingEntry}
                targetEntryId={savedTargetId || undefined}
                targetField={savedTargetField || undefined}
                isFlagged={savedIsFlagged}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </TraceabilityProvider>
  );
};
